import type { NotificationItem } from '../api/notificationsApi'
import { NOTIFICATION_VARIABLES } from '../notificationVariables'

/** The value an absent variable takes, matching the `none` ICU branch. */
export const ABSENT = 'none'

/** The metadata key that holds a failure code rather than a phrase. */
const FAILURE_REASON = 'failure_reason'
const REASON = 'reason'

/**
 * Metadata keys that hold an amount: formatted with the digit grouping and
 * decimal mark of the fan's language. Codes and names are left alone — a
 * separator would break a one-time code.
 */
const AMOUNT_KEYS: ReadonlySet<string> = new Set([
  'ap_amount',
  'xp_amount',
  'donated_amount',
  'pts_earned',
  'amount'
])

/**
 * Metadata keys that hold a moment. An RFC 3339 value is shown in the fan's
 * language and time zone; anything else (a time the service already spelled
 * out) is left as it came.
 */
const DATE_KEYS: ReadonlySet<string> = new Set(['available_at'])
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

/**
 * The tasks service sends the platform as a lowercase code; a brand is
 * written the way it writes itself, in every language. An unknown one is
 * shown as it came.
 */
const PLATFORM_NAMES: ReadonlyMap<string, string> = new Map([
  ['tiktok', 'TikTok'],
  ['instagram', 'Instagram'],
  ['youtube', 'YouTube'],
  ['facebook', 'Facebook'],
  ['x', 'X']
])
const PLATFORM = 'platform'

/** Title of a type this bundle does not know yet. */
const FALLBACK_TITLE = 'fallbackTitle'

/**
 * Metadata keys that hold a code, and the dictionary that turns it into words
 * — the same dictionaries the service uses for push and email.
 */
const CODE_DICTIONARIES: Record<string, string> = {
  task_type: 'taskTypes',
  activity_type: 'activityTypes',
  source_type: 'sourceTypes'
}

export interface NotificationText {
  title: string
  body: string
}

type Translator = (key: string, values?: Record<string, string>) => string
type HasKey = (key: string) => boolean

/**
 * Renders one feed row from its type and metadata.
 *
 * The service sends a code and the facts; the words live here, like the rest
 * of the app's copy. That is what makes the language switch in the header
 * change the feed instantly — nothing has to be re-fetched or re-rendered
 * server-side.
 *
 * A type this bundle has never heard of gets a neutral title ("New
 * notification") rather than its raw code or a blank row.
 */
export function renderNotification(
  item: NotificationItem,
  t: Translator,
  has: HasKey,
  locale?: string
): NotificationText {
  if (!has(`types.${item.type}.title`)) {
    return { title: has(FALLBACK_TITLE) ? t(FALLBACK_TITLE) : '', body: '' }
  }

  const values = toValues(item.metadata, t, has, locale)

  return {
    title: t(`types.${item.type}.title`, values),
    body: t(`types.${item.type}.body`, values)
  }
}

/**
 * Flattens metadata into ICU arguments. Every argument an ICU `select` may
 * ask about has to be present, so an absent one is passed as `none` rather
 * than left out — a missing argument throws instead of choosing a branch.
 */
function toValues(
  metadata: Record<string, unknown> | undefined,
  t: Translator,
  has: HasKey,
  locale?: string
): Record<string, string> {
  const values: Record<string, string> = {}
  const numbers = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 })
  const dates = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short'
  })

  for (const [key, raw] of Object.entries(metadata ?? {})) {
    if (raw === null || raw === undefined || typeof raw === 'object') {
      continue
    }

    const value = String(raw)
    if (value === '') {
      values[key] = ABSENT
      continue
    }

    const amount = AMOUNT_KEYS.has(key) ? Number(value) : Number.NaN
    const moment =
      DATE_KEYS.has(key) && ISO_DATE_TIME.test(value)
        ? Date.parse(value)
        : Number.NaN
    if (Number.isFinite(amount)) {
      values[key] = numbers.format(amount)
    } else if (Number.isFinite(moment)) {
      values[key] = dates.format(moment)
    } else {
      values[key] = value
    }
  }

  const platform = values[PLATFORM]
  if (platform) {
    values[PLATFORM] = PLATFORM_NAMES.get(platform.toLowerCase()) ?? platform
  }

  // A failure code is not a phrase; the dictionary turns it into one. A
  // reason the event already spells out is kept, as the service does.
  const code = values[FAILURE_REASON]
  const spelled = values[REASON]
  if (
    (!spelled || spelled === ABSENT) &&
    code &&
    code !== ABSENT &&
    has(`reasons.${code}`)
  ) {
    values[REASON] = t(`reasons.${code}`)
  }

  // A code the dictionary does not know is passed as absent, so its clause
  // disappears instead of showing the raw enum inside a translated sentence.
  for (const [key, dictionary] of Object.entries(CODE_DICTIONARIES)) {
    const value = values[key]
    if (!value || value === ABSENT) {
      continue
    }
    values[key] = has(`${dictionary}.${value}`)
      ? t(`${dictionary}.${value}`)
      : ABSENT
  }

  // Passed explicitly rather than through a Proxy: the formatter reads the
  // arguments in ways a trap does not cover, and a missing one throws instead
  // of taking the "none" branch.
  const args: Record<string, string> = {}
  for (const name of NOTIFICATION_VARIABLES) {
    args[name] = values[name] ?? ABSENT
  }

  return args
}
